import {
  Box,
  Button,
  Heading,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Tag,
  useDisclosure,
  UseDisclosureReturn,
} from '@chakra-ui/react';
import {
  createPrismaQuery,
  createPrismaSort,
  DataTable,
  DataTableAddFilter,
  DataTableFilter,
  DataTableFilters,
  DataTableSearch,
  Loosen,
  useCrud,
} from '@pelicargo/datatable';
import { RateImportState } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { getPrettyRateImportState, prettyDate, rateImportStateOptions } from '@pelicargo/utils';
import { createColumnHelper } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import { HiArrowTopRightOnSquare, HiEllipsisVertical } from 'react-icons/hi2';
import { Link } from 'react-router-dom';

import { ExpiryDrawer } from '../components/Drawers/ExpiryDrawer';
import { ExtendRateDrawer } from '../components/Drawers/ExtendRateDrawer';
import { trpc, TrpcOutputs } from '../config/trpc';

const rateFilters = [
  {
    name: 'state',
    label: 'Status',
    type: 'string',
    inputType: 'checkbox',
    operator: 'in',
    value: [],
    options: rateImportStateOptions,
  },
  {
    name: 'provider_id',
    label: 'Provider ID',
    type: 'string',
    operator: 'equals',
    inputType: 'string',
    value: '',
  },
] satisfies DataTableFilter[];

const tagColor = (state: RateImportState) => {
  switch (state) {
    case RateImportState.EXPIRED:
      return 'orange';
    case RateImportState.ERROR:
      return 'red';
    case RateImportState.RUNNING:
      return 'blue';
    case RateImportState.SUCCESS:
      return 'green';
    case RateImportState.PENDING:
    default:
      return 'gray';
  }
};

type RateImport = TrpcOutputs['getImports'][1];

const formatRateImport = (rate: RateImport[0]) => {
  return {
    ...rate,
    airline_name: rate?.provider?.name,
  };
};

type TableColumns = {
  id: number;
  provider_id: string;
  airline_name: string;
  name: string;
  source: string;
  earliest_expiry: Date;
  created_at: Date;
  state: string;
  action?: string;
};

export const RatesScreen = () => {
  const expiryDisclosure = useDisclosure();
  const extendExpiryDisclosure = useDisclosure();

  const { query, handleQueryChange, filters, setFilters, sorting, setSorting, pagination, setPagination, take, skip } =
    useCrud<typeof rateFilters>();

  const [rateImportToExpiryId, setRateImportToExpiryId] = useState<number>();

  const { data, refetch } = trpc.getImports.useQuery(
    {
      filters: createPrismaQuery(filters),
      orderBy: createPrismaSort(sorting),
      search: query,
      skip,
      take,
    },
    { refetchOnWindowFocus: false },
  );
  const count = data?.[0];
  const rateImports = data?.[1];

  const handleRowClick = useCallback(
    (row: TableColumns, disclosure: UseDisclosureReturn = expiryDisclosure) => {
      setRateImportToExpiryId(row?.id);
      disclosure.onOpen();
    },
    [expiryDisclosure, setRateImportToExpiryId],
  );

  const handleAddFilter = (filter: Loosen<typeof rateFilters>[0]) => () => {
    setFilters((prevFilters) => [...prevFilters, filter]);
  };

  const handleClear = () => {
    setFilters([]);
  };

  const handleDrawerClose = () => {
    refetch();
    expiryDisclosure.onClose();
  };

  const formattedImports = useMemo(() => rateImports?.map(formatRateImport) || [], [rateImports]);

  const rateImportToExpire = useMemo(() => {
    return rateImports?.find((node) => node?.id === rateImportToExpiryId);
  }, [rateImports, rateImportToExpiryId]);

  const columnHelper = createColumnHelper<TableColumns>();
  const columns = useMemo(
    () => [
      columnHelper.accessor('id', {
        header: 'ID',
        size: 0,
        minSize: 0,
      }),
      columnHelper.accessor('state', {
        header: 'Status',
        size: 0,
        minSize: 0,
        cell: (cell) => (
          <Tag colorScheme={tagColor(cell.getValue() as RateImportState)}>
            {getPrettyRateImportState(cell.getValue() as RateImportState)}
          </Tag>
        ),
      }),
      columnHelper.accessor('provider_id', {
        header: 'Airline ID',
        size: 0,
        minSize: 0,
      }),
      columnHelper.accessor('airline_name', {
        header: 'Airline',
      }),
      columnHelper.accessor('name', {
        header: 'Source',
      }),
      columnHelper.accessor('source', {
        header: '',
        size: 0,
        minSize: 0,
        cell: (cell) => (
          <Box onClick={(e) => e.stopPropagation()}>
            <Link to={cell?.getValue()} target="_blank">
              <Button variant="link" colorScheme="blue" rightIcon={<HiArrowTopRightOnSquare />}>
                Google Sheet
              </Button>
            </Link>
          </Box>
        ),
      }),
      columnHelper.accessor('earliest_expiry', {
        header: 'Earliest Expiry',
        size: 0,
        minSize: 0,
        cell: (cell) => {
          // TODO: Figure out how to get origin airport
          // time zone of the earliest expired rate without using hooks
          return prettyDate(cell.getValue());
        },
      }),
      columnHelper.accessor('created_at', {
        header: 'Imported At',
        size: 0,
        minSize: 0,
        cell: (cell) => prettyDate(cell.getValue() as any, { timeZone: 'Etc/UTC' }),
      }),
      columnHelper.accessor('action', {
        header: '',
        size: 0,
        minSize: 0,
        enableSorting: false,
        cell: (cell) => {
          const row = cell.row.original;
          const isExtendDisabled = ![RateImportState.SUCCESS, RateImportState.EXPIRED].includes(row?.state as any);
          const isExpireDisabled = row?.state !== RateImportState.SUCCESS;

          return (
            <Box onClick={(e) => e.stopPropagation()}>
              <Menu size="xs">
                <MenuButton
                  as={IconButton}
                  aria-label="Options"
                  size="sm"
                  icon={<HiEllipsisVertical />}
                  variant="ghost"
                />
                <MenuList>
                  <MenuItem isDisabled={isExpireDisabled} onClick={() => handleRowClick(row, expiryDisclosure)}>
                    Expire
                  </MenuItem>
                  <MenuItem isDisabled={isExtendDisabled} onClick={() => handleRowClick(row, extendExpiryDisclosure)}>
                    Extend
                  </MenuItem>
                </MenuList>
              </Menu>
            </Box>
          );
        },
      }),
    ],
    [columnHelper, expiryDisclosure, extendExpiryDisclosure, handleRowClick],
  );

  return (
    <>
      <Stack spacing="0" h="full">
        <Stack p="4" spacing="4">
          <Stack direction="row" justify="space-between" zIndex="99" w="full">
            <Stack direction="row" align="center" w="full">
              <Heading variant="h3">Rates</Heading>
              <DataTableSearch value={query} onChange={handleQueryChange} />
              <DataTableAddFilter
                activeFilters={filters?.map((filter) => filter.name)}
                options={rateFilters}
                handleAddFilter={handleAddFilter}
              />
            </Stack>
            <Link to="/rates/new">
              <Button colorScheme="brand">Create Rate</Button>
            </Link>
          </Stack>
          <Show if={filters.length > 0}>
            <DataTableFilters filters={filters} setFilters={setFilters} onClear={handleClear} />
          </Show>
        </Stack>
        <DataTable
          data={formattedImports}
          columns={columns}
          dataCount={count ?? 0}
          pagination={pagination}
          setPagination={setPagination}
          sorting={sorting}
          setSorting={setSorting}
        />
      </Stack>
      <ExpiryDrawer
        isOpen={expiryDisclosure.isOpen}
        onClose={handleDrawerClose}
        rateImportToExpire={rateImportToExpire}
      />
      <ExtendRateDrawer
        isOpen={extendExpiryDisclosure.isOpen}
        onClose={extendExpiryDisclosure.onClose}
        rateImportToExtend={rateImportToExpire}
      />
    </>
  );
};
